import { TableComponent } from '../../ui/TableComponent';
import { GraphCard } from '../../ui/GraphCard';
import { graphPurpleColor, getRankBadgeStyle, primaryText, tertiaryText, semiBoldText } from '../../../styling/stylingHelper';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { currencyFormatter } from '../../../services/helperService';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { metricIcon } from '../../ui/metricIcons';
import { useMemo, useState } from 'react';
import { TopSection } from '../../ui/TopSection';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useAppUserDefaultStore, saveVatSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterRevenueByDateRange } from '../../../utils/dateRangeFilters';
import { calculateTotalRevenue, calculateRevenueGraphData, calculateTopRevenueItems, calculateAverageRevenue, calculateRevenueMix, calculatePercentageOfTotal } from '../../../utils/revenueHistoryCalculations';
import { calculateRevenueChange } from '../../../utils/kpiCalculations';
import { buildRevenueSourceToRestaurantMap, groupRevenueByRestaurant } from '../../../utils/allRestaurantsDashboardCalculations';
import { calculateRestaurantPeriodDeltas } from '../../../utils/restaurantPeriodDeltas';
import { StatCard } from '../../ui/StatCard';
import RevenueMixCard from './RevenueMixCard';
import { ByRestaurantCard, ByRestaurantRow } from '../../ui/ByRestaurantCard';
import { RestaurantDeltaCard } from '../../revenuehistory/RestaurantDeltaCard';
import { RestaurantRevenueMixCard, RestaurantMixRow } from '../../revenuehistory/RestaurantRevenueMixCard';
import { RevenueTrendMulti } from '../../revenuehistory/RevenueTrendMulti';
import { cn } from '../../../lib/utils';
import { View } from '../../orderhistory/types';
import { filterRevenueByOverallType, getAvailableCategories, getAvailableOverallTypeOptions, getAvailableMenuCategories, getAvailableRevenueSourceNames } from '../../revenuehistory/utils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoadRevenueHistory, useLoadCostPrice } from '../../../utils/dataLoadingUtils';
import { useRevenueFilters } from '../../../hooks/useRevenueFilters';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../../../stores/appCostPriceCategoriesStore';
import { filterAllRevenue } from '../../../utils/filterUtils';
import { resetScrollToTop } from '../../../utils/scrollUtils';
import { useEffect } from 'react';
import { isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useTranslation } from 'react-i18next';
import { CategoryBadge } from '../../../utils/categoryHelper';


// Rank-badge gradient shared by the desktop Top Revenue Items table and its mobile cards.
const getTopItemRankStyle = (rank: number) => {
  if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
  if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
  if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
  return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
};

// Elevated card surface for the mobile card lists (matches the shipped Dashboard mobile cards).
const MOBILE_CARD =
  'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

const RevenueHistoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Cost price data has no time dimension here, so load it eagerly.
  useLoadCostPrice();

  // Connect to stores
  const revenueHistoryStore = useRevenueHistory();
  const showExVatRevenue = useAppUserDefaultStore(s => s.showExVat);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const restaurantRevenueSources = useRestaurantStore(s => s.restaurantRevenueSources);

  // The revenue sources in scope for this page: every restaurant's on "All Restaurants",
  // otherwise just the selected one's. Feeds both the overall-type options and the venue filter.
  const pageRevenueSources = useMemo(() => (
    isAllRestaurants
      ? Object.values(restaurantRevenueSources).flat()
      : (restaurantId ? restaurantRevenueSources[restaurantId] || [] : [])
  ), [isAllRestaurants, restaurantRevenueSources, restaurantId]);

  const costPriceStore = useCostPriceStore();
  const categoriesStore = useCostPriceCategoriesStore();

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);

  // Revenue filters hook - include all filters for this page
  const {
    filters,
    setRevenueCategoryFilters,
    setRevenueSourceFilters,
    setMenuCategoryFilters,
    resetFilters: resetRevenueFilters,
    activeFilterCount: baseActiveFilterCount,
  } = useRevenueFilters({
    includeRevenueCategories: true,
    includeRevenueSources: true,
    includeMenuCategories: true,
  });

  // Calculate total active filter count including restaurant filters
  const activeFilterCount = useMemo(() => {
    let count = baseActiveFilterCount;
    if (isAllRestaurants && restaurantFilters.length > 0) {
      count++;
    }
    return count;
  }, [baseActiveFilterCount, isAllRestaurants, restaurantFilters]);

  // Wrapper function to reset all filters including restaurant filters
  const resetFilters = () => {
    resetRevenueFilters();
    if (isAllRestaurants) {
      setRestaurantFilters([]);
    }
  };

  // Time controls - centralized logic. Declared before the data loading hook so the
  // progressive loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load data progressively: the selected window (default last week) renders first, then
  // the rest of the history streams in the background.
  useLoadRevenueHistory({ progressive: true, viewStart: startDate });

  // Progressive-loading state: "loading" means the currently viewed period hasn't fully
  // streamed in yet. The all-time view can only be trusted once everything is loaded.
  const revenueFirstChunkLoaded = useRevenueHistory(s => s.firstChunkLoaded);
  const revenueFullyLoaded = useRevenueHistory(s => s.fullyLoaded);
  const revenueLoadedFromDate = useRevenueHistory(s => s.loadedFromDate);
  const isRevenueLoading =
    !revenueFullyLoaded && (
      !revenueFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!revenueLoadedFromDate && startDate < revenueLoadedFromDate)
    );

  // Filter data by date range
  const dateFilteredRevenue = useMemo(() =>
    filterRevenueByDateRange(revenueHistoryStore.revenueHistoryList, startDate, endDate),
    [revenueHistoryStore.revenueHistoryList, startDate, endDate]
  );

  // Apply overall type filter to ALL historical data (for average calculation)
  const allHistoricalOverallTypeFiltered = useMemo(() =>
    filterRevenueByOverallType(revenueHistoryStore.revenueHistoryList, overallTypeFilter),
    [revenueHistoryStore.revenueHistoryList, overallTypeFilter]
  );

  // Same as above but ALSO run through the non-date filters (restaurant + categories) so the
  // average and the period-over-period change reflect the SAME scope as the total revenue.
  // Without this the average stays group-wide while the rest of the page narrows.
  const allHistoricalFiltered = useMemo(() =>
    filterAllRevenue(
      allHistoricalOverallTypeFiltered,
      filters,
      costPriceStore.costPriceList,
      categoriesStore.categories,
      isAllRestaurants ? restaurantFilters : undefined,
      pageRevenueSources
    ),
    [allHistoricalOverallTypeFiltered, filters, costPriceStore.costPriceList, categoriesStore.categories, isAllRestaurants, restaurantFilters, pageRevenueSources]
  );

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks');
  };

  // Handler for VAT toggle
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      // Update both settings in parallel
      await Promise.all([
        saveVatSetting(showExVat),
      ]);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Apply overall type filter, then revenue filters (revenue categories, menu categories)
  const overallTypeFilteredRevenue = useMemo(() =>
    filterRevenueByOverallType(dateFilteredRevenue, overallTypeFilter),
    [dateFilteredRevenue, overallTypeFilter]
  );

  const filteredRevenue = useMemo(() =>
    filterAllRevenue(
      overallTypeFilteredRevenue,
      filters,
      costPriceStore.costPriceList,
      categoriesStore.categories,
      isAllRestaurants ? restaurantFilters : undefined,
      pageRevenueSources
    ),
    [overallTypeFilteredRevenue, filters, costPriceStore.costPriceList, categoriesStore.categories, isAllRestaurants, restaurantFilters, pageRevenueSources]
  );

  // Calculate KPIs
  const totalRevenue = useMemo(() =>
    calculateTotalRevenue(filteredRevenue, showExVatRevenue),
    [filteredRevenue, showExVatRevenue]
  );

  const averageRevenue = useMemo(() =>
    calculateAverageRevenue(allHistoricalFiltered, showExVatRevenue, selectedTimeOption),
    [allHistoricalFiltered, showExVatRevenue, selectedTimeOption]
  );

  // Calculate revenue change from previous period (using the same filtered history as the total)
  const revenueChange = useMemo(() =>
    calculateRevenueChange(
      totalRevenue,
      selectedTimeOption as 'day' | 'week' | 'month' | 'all',
      startDate,
      endDate,
      allHistoricalFiltered,
      showExVatRevenue
    ),
    [totalRevenue, selectedTimeOption, startDate, endDate, allHistoricalFiltered, showExVatRevenue]
  );

  // Calculate revenue mix (Food/Drinks split) from time-filtered revenue (not overall type filtered)
  const revenueMix = useMemo(() =>
    calculateRevenueMix(dateFilteredRevenue, showExVatRevenue),
    [dateFilteredRevenue, showExVatRevenue]
  );

  // Calculate graph data
  const revenueGraphData = useMemo(() =>
    calculateRevenueGraphData(
      revenueHistoryStore.revenueHistoryList,
      startDate,
      endDate,
      selectedTimeOption as 'day' | 'week' | 'month' | 'quarter' | 'all',
      showExVatRevenue
    ),
    [revenueHistoryStore.revenueHistoryList, startDate, endDate, selectedTimeOption, showExVatRevenue]
  );

  // Calculate total revenue for percentage calculations
  const totalFilteredRevenue = useMemo(() =>
    calculateTotalRevenue(filteredRevenue, showExVatRevenue),
    [filteredRevenue, showExVatRevenue]
  );

  // Calculate top revenue items (show top 5 for the table with pagination)
  const topRevenueItems = useMemo(() =>
    calculateTopRevenueItems(
      filteredRevenue,
      showExVatRevenue,
      costPriceStore.costPriceList,
      categoriesStore.categories
    ).map((item, index) => ({
      ...item,
      rank: index + 1,
      percentageOfTotal: calculatePercentageOfTotal(item.totalPrice, totalFilteredRevenue),
    })),
    [filteredRevenue, showExVatRevenue, costPriceStore.costPriceList, categoriesStore.categories, totalFilteredRevenue]
  );


  // Group-wide top items (all-restaurants): aggregate by item name across every restaurant,
  // tracking how many distinct restaurants ordered it and how many distinct revenue
  // categories it appears under (the per-restaurant menu category isn't meaningful group-wide).
  const groupWideTopItems = useMemo(() => {
    if (!isAllRestaurants) return [];
    const revSourceToRestaurant = buildRevenueSourceToRestaurantMap(restaurantRevenueSources);
    const restaurantNameById = new Map(restaurants.map(r => [r.id, r.name]));
    // Cost-price category for an item name (first match), or 'Unassigned'.
    const categoryForItem = (name: string): string => {
      const analysis = costPriceStore.costPriceList.find(a => a.menuItemName === name);
      const cat = analysis?.categoryId ? categoriesStore.byId(analysis.categoryId) : null;
      return cat?.name || 'Unassigned';
    };
    const map = new Map<string, { product: string; quantity: number; total: number; rIds: Set<string> }>();
    for (const r of filteredRevenue) {
      const rId = r.restaurantId || revSourceToRestaurant[r.restaurantRevenueSourceId] || 'unknown';
      for (const category of r.revenueData.categories) {
        for (const item of category.items) {
          // No price gate — mirrors calculateTopRevenueItems: €0 and discount lines show too.
          let entry = map.get(item.name);
          if (!entry) { entry = { product: item.name, quantity: 0, total: 0, rIds: new Set() }; map.set(item.name, entry); }
          entry.quantity += item.quantity || 0;
          entry.total += showExVatRevenue ? (item.exVat || 0) : (item.price || 0);
          if (rId !== 'unknown') entry.rIds.add(rId);
        }
      }
    }
    return Array.from(map.values())
      .map(e => ({
        product: e.product,
        items: e.quantity,
        price: e.quantity > 0 ? e.total / e.quantity : 0,
        totalPrice: e.total,
        restaurantNames: Array.from(e.rIds).map(id => restaurantNameById.get(id) || id),
        menuCategory: categoryForItem(e.product),
      }))
      .sort((a, b) => b.totalPrice - a.totalPrice)
      .map((it, i) => ({ ...it, rank: i + 1, percentageOfTotal: calculatePercentageOfTotal(it.totalPrice, totalFilteredRevenue) }));
  }, [isAllRestaurants, filteredRevenue, restaurantRevenueSources, restaurants, costPriceStore.costPriceList, categoriesStore, showExVatRevenue, totalFilteredRevenue]);

  // Get available filter options from the overall-type-filtered data (before applying revenue filters)
  const availableRevenueCategories = useMemo(() =>
    getAvailableCategories(overallTypeFilteredRevenue),
    [overallTypeFilteredRevenue]
  );

  // Get available menu categories based on overall type filter
  // When "all" is selected, show all categories; otherwise, only show categories present in filtered data
  const availableMenuCategories = useMemo(() => {
    if (overallTypeFilter === 'all') {
      // Show all categories when overall type is "all"
      return categoriesStore.categories.map(cat => ({
        id: cat.id,
        name: cat.name
      }));
    } else {
      // Only show categories that exist in the overall-type-filtered data
      return getAvailableMenuCategories(
        overallTypeFilteredRevenue,
        costPriceStore.costPriceList,
        categoriesStore.categories
      );
    }
  }, [overallTypeFilter, overallTypeFilteredRevenue, costPriceStore.costPriceList, categoriesStore.categories]);

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available overall type options (base options + dynamic revenue source types)
  const availableOverallTypeOptions = useMemo(
    () => getAvailableOverallTypeOptions(pageRevenueSources),
    [pageRevenueSources]
  );

  // Venue options for the revenue source filter (distinct source names, e.g. "Untable" / "bar POP")
  const availableRevenueSources = useMemo(
    () => getAvailableRevenueSourceNames(pageRevenueSources),
    [pageRevenueSources]
  );

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    if (overallTypeFilter && !availableOverallTypeOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, availableOverallTypeOptions, setOverallTypeFilter]);

  // Drop venue selections this restaurant does not have (switching restaurant keeps the
  // store-level filter). Same idea as the overall-type reset above; skipped while the sources
  // are still loading, which would otherwise wipe a legitimate selection on first render.
  useEffect(() => {
    if (availableRevenueSources.length === 0 || filters.revenueSources.length === 0) return;
    const valid = filters.revenueSources.filter(name => availableRevenueSources.includes(name));
    if (valid.length !== filters.revenueSources.length) setRevenueSourceFilters?.(valid);
  }, [availableRevenueSources, filters.revenueSources, setRevenueSourceFilters]);

  // ──────────────────────────────────────────────────────────────────────────
  // All-restaurants sections (cross-restaurant breakdowns). Only computed/rendered
  // in all-restaurants mode; the single-restaurant branch keeps its existing sections.
  // ──────────────────────────────────────────────────────────────────────────

  // Same non-date filters as `filteredRevenue`, but over the FULL history so the
  // period-delta util can scope the current AND previous periods itself.
  const baseFilteredRevenue = useMemo(() =>
    isAllRestaurants
      ? filterAllRevenue(
          allHistoricalOverallTypeFiltered,
          filters,
          costPriceStore.costPriceList,
          categoriesStore.categories,
          restaurantFilters,
          pageRevenueSources
        )
      : [],
    [isAllRestaurants, allHistoricalOverallTypeFiltered, filters, costPriceStore.costPriceList, categoriesStore.categories, restaurantFilters, pageRevenueSources]
  );

  // Current-period revenue grouped by restaurant (active count, mix, breakdown).
  const revenueByRestaurant = useMemo((): ReturnType<typeof groupRevenueByRestaurant> => {
    if (!isAllRestaurants) return {};
    const revSourceToRestaurant = buildRevenueSourceToRestaurantMap(restaurantRevenueSources);
    return groupRevenueByRestaurant(filteredRevenue, revSourceToRestaurant);
  }, [isAllRestaurants, filteredRevenue, restaurantRevenueSources]);

  // Same grouping but WITHOUT the restaurant filter applied — feeds the trend graph's
  // multi-select so its dropdown can always offer every restaurant (the graph filters
  // to the selected ones itself, and that selection IS `restaurantFilters`).
  // Deliberately NOT date-filtered: the trend plots 12 periods ENDING at the selected one,
  // so it needs history from BEFORE the selected window — which the progressive loader
  // streams in chunk by chunk. Feeding it the date-clipped list left every earlier bucket
  // stuck at 0 (the single-restaurant graph passes the full list for the same reason).
  const revenueByRestaurantAll = useMemo((): ReturnType<typeof groupRevenueByRestaurant> => {
    if (!isAllRestaurants) return {};
    const revSourceToRestaurant = buildRevenueSourceToRestaurantMap(restaurantRevenueSources);
    const unfiltered = filterAllRevenue(
      allHistoricalOverallTypeFiltered,
      filters,
      costPriceStore.costPriceList,
      categoriesStore.categories,
      undefined,
      pageRevenueSources
    );
    return groupRevenueByRestaurant(unfiltered, revSourceToRestaurant);
  }, [isAllRestaurants, allHistoricalOverallTypeFiltered, filters, costPriceStore.costPriceList, categoriesStore.categories, restaurantRevenueSources, pageRevenueSources]);

  const activeRestaurantCount = useMemo(() =>
    Object.keys(revenueByRestaurant).filter(id => id !== 'unknown').length,
    [revenueByRestaurant]
  );

  const restaurantMixRows = useMemo<RestaurantMixRow[]>(() => {
    if (!isAllRestaurants) return [];
    const nameMap = new Map(restaurants.map(r => [r.id, r.name]));
    return Object.keys(revenueByRestaurant)
      .filter(id => id !== 'unknown')
      .map(id => ({
        restaurantId: id,
        restaurantName: nameMap.get(id) || id,
        mix: calculateRevenueMix(revenueByRestaurant[id] || [], showExVatRevenue),
        total: calculateTotalRevenue(revenueByRestaurant[id] || [], showExVatRevenue),
      }))
      .sort((a, b) => b.total - a.total)
      .map(({ restaurantId, restaurantName, mix }) => ({ restaurantId, restaurantName, mix }));
  }, [isAllRestaurants, revenueByRestaurant, restaurants, showExVatRevenue]);

  // Per-restaurant current-vs-previous revenue deltas (Top Riser / Biggest Faller / ranking).
  const periodDeltas = useMemo(() =>
    isAllRestaurants
      ? calculateRestaurantPeriodDeltas(
          baseFilteredRevenue,
          selectedTimeOption as View,
          startDate,
          endDate,
          restaurants,
          restaurantRevenueSources,
          showExVatRevenue
        )
      : [],
    [isAllRestaurants, baseFilteredRevenue, selectedTimeOption, startDate, endDate, restaurants, restaurantRevenueSources, showExVatRevenue]
  );

  // Risers = genuine positive growth; fallers = genuine decline. Restaurants with no
  // change (or no current data → not a "riser") are excluded so an empty/future period
  // doesn't fill both cards with "-100%".
  const topRisers = useMemo(() =>
    periodDeltas.filter(d => d.current > 0 && d.changePercent > 0).slice(0, 5),
    [periodDeltas]
  );
  const topFallers = useMemo(() =>
    periodDeltas.filter(d => d.current > 0 && d.changePercent < 0).slice(-5).reverse(),
    [periodDeltas]
  );

  // Restaurant ranking table: all restaurants by current-period revenue (with search).
  const [rankingSearch, setRankingSearch] = useState('');
  // Mobile-only: cap how many ranking cards render at once; grow on "Load more" (never map the full list).
  const [rankingMobileVisible, setRankingMobileVisible] = useState(5);
  const rankingRows = useMemo(() => {
    const totalCurrent = periodDeltas.reduce((sum, d) => sum + d.current, 0);
    return [...periodDeltas]
      .filter(d => d.current > 0)
      .sort((a, b) => b.current - a.current)
      .map((d, i) => {
        const mix = calculateRevenueMix(revenueByRestaurant[d.restaurantId] || [], showExVatRevenue);
        return {
          ...d,
          rank: i + 1,
          food: mix.food.value,
          drinks: mix.drinks.value,
          revenueShare: totalCurrent > 0 ? (d.current / totalCurrent) * 100 : 0,
        };
      });
  }, [periodDeltas, revenueByRestaurant, showExVatRevenue]);
  const filteredRankingRows = useMemo(() => {
    const q = rankingSearch.trim().toLowerCase();
    if (!q) return rankingRows;
    return rankingRows.filter(r => r.restaurantName.toLowerCase().includes(q));
  }, [rankingRows, rankingSearch]);

  // Revenue-by-restaurant rows: share of total (bar) + period-over-period change pill.
  // The 'all' view has no previous period, so the change pill is hidden.
  const revenueByRestaurantRows = useMemo<ByRestaurantRow[]>(() => {
    const totalCurrent = periodDeltas.reduce((sum, d) => sum + d.current, 0);
    const hideChange = selectedTimeOption === 'all';
    return [...periodDeltas]
      .filter(d => d.current > 0)
      .sort((a, b) => b.current - a.current)
      .map(d => ({
        restaurantId: d.restaurantId,
        restaurantName: d.restaurantName,
        value: d.current,
        percentage: totalCurrent > 0 ? (d.current / totalCurrent) * 100 : 0,
        changePercent: hideChange ? null : d.changePercent,
      }));
  }, [periodDeltas, selectedTimeOption]);

  // Revenue-by-restaurant ExpandModal — page-owned so the `?expand=` deep link can open it.
  const [revByRestaurantModalOpen, setRevByRestaurantModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('expand') === 'revenueByRestaurant') {
      setRevByRestaurantModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('expand');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div>
      <MobilePageHeader title={t('revenueHistory.title')} right={<RestaurantSelector />} />

      <TopSection
          title={t('revenueHistory.title')}
          backButton={false}
          dateRange={dateRange}
          setDateRange={setDateRange}
          selectedTimeOption={selectedTimeOption}
          setSelectedTimeOption={setSelectedTimeOption}
          onOverallTypeChange={handleOverallTypeChange}
          currentOverallTypeValue={overallTypeFilter}
          overallTypeOptions={availableOverallTypeOptions}
          onVatChange={handleVatChange}
          currentVatValue={showExVatRevenue ? 'excl' : 'incl'}
          showFilters={true}
          filters={{
            revenueFilters: {
              restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
              setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
              availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
              revenueCategoryFilters: filters.revenueCategories,
              setRevenueCategoryFilters,
              availableRevenueCategories,
              revenueSourceFilters: filters.revenueSources,
              setRevenueSourceFilters,
              availableRevenueSources,
              menuCategoryFilters: filters.menuCategories,
              setMenuCategoryFilters,
              availableMenuCategories,
              onResetFilters: resetFilters,
              activeFilterCount,
            }
          }}
        />

        {/* Responsive layout group for content sections below 1250px */}
      <div className="mt-4 mb-4 flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below. On phones: 2-col compact grid
            when 4 cards (all restaurants), stacked compact rows when 3. */}
        <div className={cn('grid gap-3 md:gap-6 sm:grid-cols-2', isAllRestaurants ? 'grid-cols-2 xl:grid-cols-4' : 'grid-cols-1 xl:grid-cols-3')}>
          <div className="w-full" data-onboarding="page-kpis" data-onboarding-ready={!isRevenueLoading}>
            <StatCard
              {...metricIcon('revenue')}
              title={isAllRestaurants ? t('revenueHistory.totalGroupRevenue') : t('revenueHistory.totalRevenue')}
              value={totalRevenue}
              valueType="currency"
              changeValue={revenueChange?.changeValue}
              changeLabel={revenueChange?.changeLabel}
              trendDirection="up"
              loading={isRevenueLoading}
              mobileLayout={isAllRestaurants ? 'tile' : 'row'}
            />
          </div>
          <div className="w-full" data-onboarding="page-kpis" data-onboarding-ready={!isRevenueLoading}>
            <StatCard
              {...metricIcon('avgRevenue')}
              title={t('revenueHistory.averageRevenue')}
              value={averageRevenue}
              valueType="currency"
              loading={isRevenueLoading}
              mobileLayout={isAllRestaurants ? 'tile' : 'row'}
            />
          </div>
          {isAllRestaurants && (
            <div className="w-full" data-onboarding="page-kpis" data-onboarding-ready={!isRevenueLoading}>
              <StatCard
                {...metricIcon('activeRestaurants')}
                title={t('revenueHistory.activeRestaurants')}
                value={activeRestaurantCount}
                valueType="number"
                loading={isRevenueLoading}
                mobileLayout="tile"
              />
            </div>
          )}
          <div className="w-full" data-onboarding="page-kpis" data-onboarding-ready={!isRevenueLoading}>
            <RevenueMixCard revenueMix={revenueMix} loading={isRevenueLoading} mobileLayout={isAllRestaurants ? 'tile' : 'row'} />
          </div>
        </div>

      {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {isAllRestaurants ? (
          <>
            {/* Multi-restaurant revenue trend + revenue-by-restaurant */}
            <div className="flex flex-col gap-6 min-[1250px]:flex-row">
              <div className="w-full min-[1250px]:w-[67.5%]">
                <RevenueTrendMulti
                  revenueByRestaurant={revenueByRestaurantAll}
                  restaurants={restaurants}
                  startDate={startDate}
                  endDate={endDate}
                  view={selectedTimeOption as View}
                  showExVat={showExVatRevenue}
                  selectedRestaurantIds={restaurantFilters}
                  setSelectedRestaurantIds={setRestaurantFilters}
                />
              </div>
              <div className="w-full min-[1250px]:w-[32.5%]">
                <ByRestaurantCard
                  title={t('revenueHistory.revenueByRestaurant')}
                  data={revenueByRestaurantRows}
                  loading={isRevenueLoading}
                  isModalOpen={revByRestaurantModalOpen}
                  onMaximize={() => setRevByRestaurantModalOpen(true)}
                  onCloseModal={() => setRevByRestaurantModalOpen(false)}
                />
              </div>
            </div>

            {/* Top Riser / Biggest Faller / Food-Drink mix */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <RestaurantDeltaCard
                title={t('revenueHistory.topRiser')}
                subtitle={t('revenueHistory.topRiserSubtitle')}
                icon={<ArrowUpRight className="w-5 h-5" style={{ color: '#0E8965' }} />}
                iconBg="#0E89651A"
                changeColor="#079455"
                items={topRisers}
                loading={isRevenueLoading}
              />
              <RestaurantDeltaCard
                title={t('revenueHistory.biggestFaller')}
                subtitle={t('revenueHistory.biggestFallerSubtitle')}
                icon={<ArrowDownRight className="w-5 h-5" style={{ color: '#D92D20' }} />}
                iconBg="#D92D201A"
                changeColor="#D92D20"
                items={topFallers}
                loading={isRevenueLoading}
              />
              <RestaurantRevenueMixCard items={restaurantMixRows} overall={revenueMix} loading={isRevenueLoading} />
            </div>

            {/* Restaurant ranking table (inline pagination + search) */}
            <div className="w-full">
              <ResponsiveTable
                desktop={<TableComponent
                title={t('revenueHistory.restaurantRanking')}
                data={filteredRankingRows}
                blankLines={false}
                usePagination={true}
                searchQuery={rankingSearch}
                onSearchChange={setRankingSearch}
                searchPlaceholder={t('revenueHistory.searchRestaurants')}
                columns={[
                  {
                    key: 'restaurantName',
                    label: t('revenueHistory.restaurant'),
                    columnType: 'name',
                    render: (value: string, row: any) => (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                          style={getRankBadgeStyle(row.rank || 1)}
                        >
                          {row.rank || 1}
                        </div>
                        <span className="text-textPrimary dark:text-textPrimary-dark text-[14px] leading-5 font-medium truncate">
                          {String(value)}
                        </span>
                      </div>
                    ),
                  },
                  {
                    key: 'current',
                    label: t('revenueHistory.revenue'),
                    columnType: 'base',
                    render: (value) => (
                      <span className="text-textPrimary dark:text-textPrimary-dark text-[14px] font-medium">
                        {currencyFormatter.format(value as number)}
                      </span>
                    ),
                  },
                  {
                    key: 'changePercent',
                    label: t('revenueHistory.change'),
                    columnType: 'base',
                    render: (value) => {
                      const pct = value as number;
                      const up = pct >= 0;
                      return (
                        <span
                          className={cn(
                            'inline-block text-[14px] leading-none font-semibold px-2 py-1 rounded-[6px]',
                            up
                              ? 'bg-[#069A6E]/10 text-[#069A6E] dark:bg-green-500/10 dark:text-textGreen-dark'
                              : 'bg-[#D92D20]/10 text-[#D92D20] dark:bg-red-500/10 dark:text-textRed-dark'
                          )}
                        >
                          {up ? '+' : ''}{pct.toFixed(1)}%
                        </span>
                      );
                    },
                  },
                  {
                    key: 'food',
                    label: t('revenueHistory.food'),
                    columnType: 'base',
                    render: (value) => (
                      <span className="text-textSecondary dark:text-textSecondary-dark text-[14px]">
                        {currencyFormatter.format(value as number)}
                      </span>
                    ),
                  },
                  {
                    key: 'drinks',
                    label: t('revenueHistory.drinks'),
                    columnType: 'base',
                    render: (value) => (
                      <span className="text-textSecondary dark:text-textSecondary-dark text-[14px]">
                        {currencyFormatter.format(value as number)}
                      </span>
                    ),
                  },
                  {
                    key: 'revenueShare',
                    label: t('dashboard.percentOfRevenue'),
                    columnType: 'base',
                    render: (value) => (
                      <div className="flex items-center gap-2">
                        <div className="w-[40px] h-[7px] rounded-full bg-[#D1D1D6] dark:bg-gray-700 overflow-hidden flex-shrink-0">
                          <div className="h-full rounded-full bg-[#0E8965] dark:bg-darkGreenColor-dark" style={{ width: `${Math.min(value as number, 100)}%` }} />
                        </div>
                        <span className="text-[#344054] dark:text-textPrimary-dark text-[14px] font-semibold">
                          {(value as number).toFixed(1)}%
                        </span>
                      </div>
                    ),
                  },
                ]}
              />}
              mobile={
                <div>
                  <h3 className={cn('text-[18px] leading-6 mb-4', semiBoldText, primaryText)}>{t('revenueHistory.restaurantRanking')}</h3>
                  <MobileCardList
                    items={filteredRankingRows.slice(0, rankingMobileVisible)}
                    keyOf={(row) => row.restaurantId}
                    onLoadMore={() => setRankingMobileVisible(v => v + 5)}
                    hasMore={rankingMobileVisible < filteredRankingRows.length}
                    loadMoreLabel={t('revenueHistory.loadMore')}
                    renderCard={(row) => (
                      <div className={cn(MOBILE_CARD, 'flex flex-col')}>
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                              style={getRankBadgeStyle(row.rank || 1)}
                            >
                              {row.rank || 1}
                            </div>
                            <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)}>{row.restaurantName}</span>
                          </div>
                          <span
                            className={cn(
                              'inline-block text-[14px] leading-none font-semibold px-2 py-1 rounded-[6px] flex-shrink-0',
                              row.changePercent >= 0
                                ? 'bg-[#069A6E]/10 text-[#069A6E] dark:bg-green-500/10 dark:text-textGreen-dark'
                                : 'bg-[#D92D20]/10 text-[#D92D20] dark:bg-red-500/10 dark:text-textRed-dark'
                            )}
                          >
                            {row.changePercent >= 0 ? '+' : ''}{row.changePercent.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-stretch">
                          <MobileCardField label={t('revenueHistory.food')} value={currencyFormatter.format(row.food)} divider />
                          <MobileCardField label={t('revenueHistory.drinks')} value={currencyFormatter.format(row.drinks)} divider />
                          <MobileCardField label={t('dashboard.percentOfRevenue')} value={`${row.revenueShare.toFixed(1)}%`} />
                        </div>
                        <div className="mt-3 pt-3 border-t border-border dark:border-border-dark flex items-center justify-between">
                          <span className={cn('text-xs font-medium', tertiaryText)}>{t('revenueHistory.revenue')}</span>
                          <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format(row.current)}</span>
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
            </div>
          </>
        ) : (
          <div className="w-full">
            <GraphCard
              title={t('revenueHistory.revenueSpendTrends')}
              xLabels={revenueGraphData.map(d => d.label)}
              series={[
                { label: t('revenueHistory.revenue'), values: revenueGraphData.map(d => d.value), color: graphPurpleColor },
              ]}
              height={330}
            />
          </div>
        )}

          {/* Top Revenue Items Table */}
          <div className="w-full" data-onboarding="revenue-top-items" data-onboarding-ready={!isRevenueLoading}>
            <ResponsiveTable
              desktop={<TableComponent
                title={t('revenueHistory.topRevenueItems')}
                data={(isAllRestaurants ? groupWideTopItems : topRevenueItems) as any[]}
                blankLines={true}
                columns={[
                  {
                    key: 'product',
                    label: t('revenueHistory.rankAndDishName'),
                    columnType: 'name',
                    render: (value: string, row: any) => {
                      const rank = row.rank || 1;
                      const getRankStyle = getTopItemRankStyle;
                      return (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
                            style={getRankStyle(rank)}
                          >
                            {rank}
                          </div>
                          <span className="text-[#101828] dark:text-textPrimary-dark text-[14px] leading-5 font-medium">
                            {String(value)}
                          </span>
                        </div>
                      );
                    }
                  },
                  // All-restaurants: how many restaurants ordered it + how many revenue categories
                  // it appears under. Single-restaurant: the dish's menu category badge.
                  ...(isAllRestaurants ? [
                    {
                      key: 'restaurantNames',
                      label: t('revenueHistory.restaurantsOrdered'),
                      columnType: 'base' as const,
                      render: (value: any) => {
                        const names: string[] = Array.isArray(value) ? value : [];
                        if (names.length === 0) return <span className="text-textTertiary dark:text-textTertiary-dark text-[14px]">--</span>;
                        if (names.length === 1) return <span className="text-textTertiary dark:text-textTertiary-dark text-[14px]">{names[0]}</span>;
                        return (
                          <span
                            className="text-textTertiary dark:text-textTertiary-dark text-[14px] cursor-help inline-block"
                            title={names.join(', ')}
                            style={{ borderBottom: '1px dotted currentColor' }}
                          >
                            {names.length} {t('orderHistory.restaurantsShort')}
                          </span>
                        );
                      },
                    },
                    {
                      key: 'menuCategory',
                      label: t('revenueHistory.category'),
                      columnType: 'base' as const,
                      render: (value: any, row: any) => {
                        const name = value || 'Unassigned';
                        const analysis = costPriceStore.costPriceList.find(a => a.menuItemName === row.product);
                        const category = analysis?.categoryId ? categoriesStore.byId(analysis.categoryId) : null;
                        return (
                          <CategoryBadge
                            categoryName={name}
                            categoryColor={category?.color}
                            isDefaultCategory={category?.isDefault || false}
                          />
                        );
                      },
                    },
                  ] : [
                    {
                      key: 'menuCategory',
                      label: t('revenueHistory.category'),
                      render: (value: any, row: any) => {
                        if (!value) return null;
                        const currentAnalysis = costPriceStore.costPriceList.find(
                          analysis => analysis.menuItemName === row.product
                        );
                        const category = currentAnalysis?.categoryId
                          ? categoriesStore.byId(currentAnalysis.categoryId)
                          : null;
                        const categoryColor = category?.color;
                        const isDefaultCategory = category?.isDefault || false;
                        return (
                          <CategoryBadge
                            categoryName={value}
                            categoryColor={categoryColor}
                            isDefaultCategory={isDefaultCategory}
                          />
                        );
                      }
                    },
                  ]),
                  { key: 'items', label: t('revenueHistory.items'), columnType: 'blueQuantity' },
                  { key: 'price', label: t('revenueHistory.price'), columnType: 'currency' },
                  { key: 'totalPrice', label: t('revenueHistory.totalPrice'), columnType: 'currencyHighlight' },
                  {
                    key: 'percentageOfTotal',
                    label: t('revenueHistory.percentOfTotal'),
                    columnType: 'base',
                    render: (value) => {
                      const percentage = value as number;
                      return (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[#F2F4F7] dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#0E8965] dark:bg-[#0E8965] rounded-full"
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                          <span className="text-[#0C111D] dark:text-textPrimary-dark text-[12px] leading-[18px] font-semibold">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                      );
                    }
                  },
                ]}
                seeAllText={t('revenueHistory.showAll')}
                seeAllOnboardingId="revenue-see-all"
                onSeeAllClick={() => {navigate('/revenuehistory/items')}}
              />}
              mobile={
                <div>
                  <h3 className={cn('text-[18px] leading-6 mb-4', semiBoldText, primaryText)}>{t('revenueHistory.topRevenueItems')}</h3>
                  <MobileCardList
                    items={(isAllRestaurants ? groupWideTopItems : topRevenueItems) as any[]}
                    keyOf={(item, i) => `${item.product}-${i}`}
                    previewCount={3}
                    onViewAll={() => navigate('/revenuehistory/items')}
                    viewAllLabel={t('revenueHistory.showAll')}
                    renderCard={(item) => (
                      <div className={cn(MOBILE_CARD, 'flex flex-col gap-3')}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                              style={getTopItemRankStyle(item.rank || 1)}
                            >
                              {item.rank || 1}
                            </div>
                            <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)}>{item.product}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn('text-xs font-medium', tertiaryText)}>{t('revenueHistory.revenue')}</span>
                            <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format(item.totalPrice)}</span>
                          </div>
                        </div>
                        <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                          <MobileCardField label={t('revenueHistory.items')} value={String(item.items)} divider />
                          <MobileCardField label={t('revenueHistory.price')} value={currencyFormatter.format(item.price)} divider />
                          <MobileCardField label={t('revenueHistory.percentOfTotal')} value={`${(item.percentageOfTotal ?? 0).toFixed(1)}%`} />
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueHistoryPage;
